import { FormInput, InfoBox } from 'components/molecules';
import LabeledActiveStatus from 'components/molecules/labeledStatus/LabeledActiveStatus';
import TopBar from 'components/organisms/top-bar/TopBar';
import { TopbarTabs } from 'components/templates';
import { Form, Formik, FormikConfig } from 'formik';
import i18next from 'i18next';
import { useTranslation } from 'react-i18next';
import { ICreateEmail } from 'services/email/types';
import { ACTIVE_STATUS } from 'services/types';
import { number, object, string } from 'yup';
import { IFormikProps, IFormProps } from '../types';

const validationSchema = object({}).shape({
  name: string().required(() => i18next.t('formik-error.required')),
  mailLabel: string().required(() => i18next.t('formik-error.required')),
  email: string()
    .email(i18next.t('formik-error.invalid-email'))
    .required(i18next.t('formik-error.required')),
  host: string().required(() => i18next.t('formik-error.required')),
  username: string().required(() => i18next.t('formik-error.required')),
  password: string().required(() => i18next.t('formik-error.required')),
  port: number().required(() => i18next.t('formik-error.required')),
});

export const emailFormInitialValues: ICreateEmail = {
  name: '',
  mailLabel: '',
  email: '',
  host: '',
  port: 0,
  enableSSL: true,
  username: '',
  password: '',
  isActive: true,
};

interface IEmailFormProps extends FormikConfig<ICreateEmail>, IFormProps {}

const EmailForm = ({
  customBackItem,
  status,
  tittle,
  backPath,
  topbarContent,
  disableTabs,
  initialValues,
  ...props
}: IEmailFormProps) => {
  const { t } = useTranslation();
  return (
    <Formik
      initialValues={initialValues}
      validationSchema={validationSchema}
      validateOnChange={true}
      validateOnBlur={true}
      initialStatus={status}
      enableReinitialize
      {...props}>
      {({ values, setFieldValue }: IFormikProps<ICreateEmail>) => (
        <Form className='w-full h-full'>
          <TopbarTabs>
            <TopBar
              customBackItem={customBackItem}
              title={status?.displayMode ? tittle : undefined}
              path={backPath ?? 'label'}
              topbarContent={
                <div className='flex w-full items-center justify-end'>
                  {status?.displayMode ? null : (
                    <div className='flex w-full justify-between mr-4'>
                      <div className='w-60'>
                        <FormInput.Text
                          name='name'
                          label={t('input.label.name')}
                          autoFocus
                        />
                      </div>
                      <div className='w-40'>
                        <FormInput.Select
                          name='isActive'
                          label={t('input.label.isActive')}
                          isClearable={false}
                          options={[
                            { label: t('input.option.yes'), value: true },
                            { label: t('input.option.no'), value: false },
                          ]}
                        />
                      </div>
                    </div>
                  )}
                  <div className='flex gap-4 items-center'>
                    {status?.displayMode && (
                      <div>
                        <LabeledActiveStatus
                          status={
                            values.isActive
                              ? ACTIVE_STATUS.Active
                              : ACTIVE_STATUS.Inactive
                          }
                        />
                      </div>
                    )}
                    {topbarContent}
                  </div>
                </div>
              }>
              <InfoBox loading={status?.isLoading}>
                <InfoBox.Items columns={3}>
                  <FormInput.Text
                    name='mailLabel'
                    label={t('input.label.mailLabel')}
                  />
                  <FormInput.Text name='email' label={t('input.label.email')} />
                  <FormInput.Text name='host' label={t('input.label.host')} />
                  <FormInput.Number name='port' label={t('input.label.port')} />
                  <FormInput.Text
                    name='username'
                    label={t('input.label.username')}
                  />
                  <FormInput.Text
                    name='password'
                    label={t('input.label.password')}
                  />
                  <FormInput.Select
                    name='enableSSL'
                    label={t('input.label.enableSSL')}
                    isClearable={false}
                    options={[
                      { label: t('input.option.yes'), value: true },
                      { label: t('input.option.no'), value: false },
                    ]}
                  />
                </InfoBox.Items>
              </InfoBox>
            </TopBar>
          </TopbarTabs>
        </Form>
      )}
    </Formik>
  );
};

export default EmailForm;
